import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react'
import { useParams, useLocation, Outlet } from 'react-router-dom'
import useTenant360Store from '../store/tenant360Store'
import useTenantPermissions, { TenantPermissions } from '../permissions/useTenantPermissions'
import { TenantDetail, CustomerSuccessOption, SetupBusiness, PosDetail, SetupPipelineStep, StageProgressMap, StageNavItem, NotificationVendorGroup, OnboardingUserSummary, ModuleCatalogEntry } from '../types/types'
import { TenantOnboardStep } from '../../TenantForm/constants/tenantOnboardEnums'
import { TenantSetupStatusResponse } from '../types/types'
import SharedLayout from '../../Sidebar/components/SharedLayout'

export interface Tenant360ContextValue {
    tenantId: number
    tenantDisplayName: string
    tenantDetails: TenantDetail | null
    businesses: SetupBusiness[]
    metaData: TenantSetupStatusResponse['metaData'] | null
    posDetails: PosDetail[]
    tenantSubscribedModules: string[]
    businessSubscribedModules: Record<number, string[]>
    businessNewModules: Record<number, string[]>
    notificationVendors: NotificationVendorGroup[]
    onboardingUsers: OnboardingUserSummary[]
    moduleCatalog: ModuleCatalogEntry[]
    defaultOirModules: ModuleCatalogEntry[]
    onboardingForumLink: string | null
    customerSuccessOptions: CustomerSuccessOption[]
    fetchCustomerSuccessOptions: () => void
    isLoading: boolean
    error: string | null
    permissions: TenantPermissions
    refreshStatus: () => Promise<void>
    completedStages: string[]
    completedSteps: string[]
    businessStepsProgress: StageProgressMap
    getStageNavItems: () => StageNavItem[]
    isStageComplete: (stage: TenantOnboardStep, businessId?: number) => boolean
    subscriptionPipeline: SetupPipelineStep[]
    subscriptionPipelineRunning: boolean
    retrySubscriptionPipeline: () => void
}

const Tenant360Context = createContext<Tenant360ContextValue | null>(null)

export const useTenant360 = (): Tenant360ContextValue => {
    const ctx = useContext(Tenant360Context)
    if (!ctx) {
        throw new Error(
            'useTenant360 must be used within a <Tenant360Provider>. ' +
            'Make sure the component is rendered inside a /tenants/:tenantId route.'
        )
    }
    return ctx
}

export const Tenant360Provider: React.FC = () => {
    const { tenantId: tenantIdParam } = useParams<{ tenantId: string }>()
    const parsedId = Number(tenantIdParam)
    const location = useLocation()

    // /landing endpoint returns richer data (modules + vendors); sub-pages use /status
    const isLandingRoute =
        location.pathname.replace(/\/+$/, '') === `/tenants/${tenantIdParam}`

    const {
        fetchStatus,
        reset,
        tenantDetails,
        businesses,
        metaData,
        posDetails,
        tenantSubscribedModules,
        businessSubscribedModules,
        businessNewModules,
        notificationVendors,
        onboardingUsers,
        moduleCatalog,
        defaultOirModules,
        onboardingForumLink,
        customerSuccessOptions,
        fetchCustomerSuccessOptions,
        statusLoading,
        statusError,
        completedStages,
        completedSteps,
        businessStepsProgress,
        getStageNavItems,
        isStageComplete,
        subscriptionPipeline,
        subscriptionPipelineRunning,
        retrySubscriptionPipeline,
    } = useTenant360Store()

    const permissions = useTenantPermissions()

    useEffect(() => {
        if (!parsedId || isNaN(parsedId)) return
        reset()
        fetchStatus(parsedId, isLandingRoute)
        if (isLandingRoute) {
            fetchCustomerSuccessOptions()
        }
    }, [parsedId, isLandingRoute]) // eslint-disable-line react-hooks/exhaustive-deps

    // If the user navigated directly to a sub-page first (/status fetched, no modules/vendors),
    // re-fetch with /landing once they reach the landing route — but only when not already loading.
    useEffect(() => {
        if (!parsedId || isNaN(parsedId) || !isLandingRoute || statusLoading) return
        if (tenantSubscribedModules.length === 0) {
            fetchStatus(parsedId, true)
            fetchCustomerSuccessOptions()
        }
    }, [isLandingRoute]) // eslint-disable-line react-hooks/exhaustive-deps

    const refreshStatus = useCallback(async () => {
        if (parsedId) {
            await fetchStatus(parsedId, isLandingRoute)
            if (isLandingRoute) {
                await fetchCustomerSuccessOptions()
            }
        }
    }, [parsedId, isLandingRoute, fetchStatus, fetchCustomerSuccessOptions])

    const tenantDisplayName =
        tenantDetails?.esKey ?? metaData?.esKey ?? `Tenant #${parsedId}`

    const value = useMemo<Tenant360ContextValue>(
        () => ({
            tenantId: parsedId,
            tenantDisplayName,
            tenantDetails,
            businesses,
            metaData,
            posDetails,
            tenantSubscribedModules,
            businessSubscribedModules,
            businessNewModules,
            notificationVendors,
            onboardingUsers,
            moduleCatalog,
            defaultOirModules,
            onboardingForumLink,
            customerSuccessOptions,
            fetchCustomerSuccessOptions,
            isLoading: statusLoading,
            error: statusError,
            permissions,
            refreshStatus,
            completedStages,
            completedSteps,
            businessStepsProgress,
            getStageNavItems,
            isStageComplete,
            subscriptionPipeline,
            subscriptionPipelineRunning,
            retrySubscriptionPipeline,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            parsedId,
            tenantDisplayName,
            tenantDetails,
            businesses,
            metaData,
            posDetails,
            tenantSubscribedModules,
            businessSubscribedModules,
            businessNewModules,
            notificationVendors,
            onboardingUsers,
            moduleCatalog,
            defaultOirModules,
            onboardingForumLink,
            customerSuccessOptions,
            fetchCustomerSuccessOptions,
            statusLoading,
            statusError,
            permissions,
            refreshStatus,
            completedStages,
            completedSteps,
            businessStepsProgress,
            subscriptionPipeline,
            subscriptionPipelineRunning,
            retrySubscriptionPipeline,
        ]
    )

    return (
        <Tenant360Context.Provider value={value}>
            <SharedLayout>
                <Outlet />
            </SharedLayout>
        </Tenant360Context.Provider>
    )
}

export default Tenant360Context
